import React from 'react';
import type { AnywhereType, BuildoutType, ListingMatch } from '../types';
import { Tooltip, OverlayTrigger } from 'react-bootstrap-5';
import confirm from 'components/confirm';
import { setListingGuid } from '../api';
import { connect } from 'react-redux';
import { setFlash, updateListingGuid } from '../store/actions';
import { anywhereTypeLabel, buildoutTypeLabel } from '../helpers';
import Button from 'shared/button';
import CollapsibleSection from './collapsible_section';
import Filters from './filters';

type MatchedListingsTableProps = {
  matchedListings: ListingMatch[];
  setFlash: (message?: string | null, variant?: string) => void;
  updateListingGuid: (guid: string, buildoutListingId: number) => void;
}

function MatchedListingsTable({ matchedListings, setFlash, updateListingGuid } : MatchedListingsTableProps) {
  const handleRemoveGuid = (guid: string, listingId: number) => {
    confirm(`Are you sure you want to remove the GUID ${guid} from listing ID ${listingId}?`, {
      buttonLabel: 'Delete',
      buttonStyle: 'danger',
      title: `Delete Date?`
    }).then(() => {
      setListingGuid(listingId, null).then(() => {
        updateListingGuid('', listingId);
        setFlash('GUID removed successfully!', 'success');
      });
    });
  };

  const renderTypeColumns = (anywhereType: AnywhereType, buildoutType: BuildoutType, guid: string) => {
    const buildoutLabel = buildoutTypeLabel(buildoutType);
    const anywhereLabel = anywhereTypeLabel(anywhereType);

    if (buildoutLabel == anywhereLabel) return (
      <React.Fragment>
        <td className="bg-success">{anywhereLabel}</td>
        <td className="bg-success">{buildoutLabel}</td>
      </React.Fragment>
    );

    const tooltip = (
      <Tooltip id={`tooltip-${guid}`}>
        <span>This listing type does not match</span>
      </Tooltip>
    );
    return (
      <React.Fragment>
        <td className="bg-danger">
          <OverlayTrigger overlay={tooltip} placement="bottom">
            <span>{anywhereLabel}</span>
          </OverlayTrigger>
        </td>
        <td className="bg-danger">
          <OverlayTrigger overlay={tooltip} placement="bottom">
            <span>{buildoutLabel}</span>
          </OverlayTrigger>
        </td>
      </React.Fragment>
    );
  };

  const table = (
    <table className="table table-bordered table-striped">
      <thead>
        <tr>
          <th>Buildout Listing ID</th>
          <th>DASH Listing ID</th>
          <th>GUID</th>
          <th>DASH Type</th>
          <th>BO Type</th>
          <th>DASH Location</th>
          <th>BO Location</th>
          <th>Remove GUID</th>
        </tr>
      </thead>
      <tbody>
        {matchedListings.map(match => (
          <tr key={match.anywhereListingId}>
            <td>{match.buildoutListingId}</td>
            <td>{match.anywhereListingId}</td>
            <td>{match.guid}</td>
            {renderTypeColumns(match.anywhereListing.typeDescription, match.boListing.class, match.guid)}
            <td>
              {match.anywhereListing.propertyLocation.addressLine1},&nbsp;
              {match.anywhereListing.propertyLocation.city},&nbsp;
              {match.anywhereListing.propertyLocation.stateProvinceName}&nbsp;
              {match.anywhereListing.propertyLocation.postalCode}
            </td>
            <td>{match.boListing.address}, {match.boListing.city}, {match.boListing.state} {match.boListing.zip}</td>
            <td>
              <Button.Danger
                label="Remove"
                onClick={() => handleRemoveGuid(match.guid, match.buildoutListingId)}
              />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  return (
    <CollapsibleSection count={matchedListings.length} title="Matched Listings">
      <Filters />
      {table}
    </CollapsibleSection>
  );
}

const mapStateToProps = ({ listings: { matchedListings } }) => ({ matchedListings });

export default connect(mapStateToProps, { updateListingGuid, setFlash })(MatchedListingsTable);
